// Dependencies
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { FC, useRef, useState } from "react";

// Components
import IconInput from "../IconInput";
import FormItem from "~/components/Layout/LayoutForm/FormItem";
import Button, { WarningButton } from "~/components/Common/Button";
import { MenuItem } from "@mui/material";
import { If } from "jsx-control-statements";

// types
import { TCategoryData } from "~/types";
import { DefaultCategory } from "~/enums";

// Styles
import {
  StyledInfoDetail,
  StyledInfoWrapper,
  StyledInfoContent,
} from "~/components/Layout/LayoutInfo";
import {
  StyledUnsaved,
  StyledFormInput,
  StyledFormSelect,
  StyledFormAction,
} from "~/components/Layout/LayoutForm/Form.styled";
import { StyledAddLabel } from "~/components/Layout/LayoutAdd";
import { handleCancelForm } from "~/utils";

interface CategoryAddProps {
  parentList: TCategoryData[];
  onSubmit: Function;
  onCancel: Function;
}

const categoryTypeList = ["debt", "expense", "income", "loan"];

const CategoryAdd: FC<CategoryAddProps> = ({
  parentList,
  onSubmit,
  onCancel,
}) => {
  const [categoryType, setCategoryType] = useState<
    (typeof categoryTypeList)[number]
  >(categoryTypeList[0]);

  const formRef = useRef<any>(null);

  // Methods
  const handleSubmitForm = (values: {
    name: string;
    type: string;
    iconId: string;
    parentId?: string;
  }) => {
    onSubmit(values);
  };

  const typeList = categoryTypeList.map((type) => (
    <MenuItem value={type} key={type}>
      {type}
    </MenuItem>
  ));

  const categoryList = parentList
    .find((parent) => parent.type === categoryType)
    ?.list.map((item) => (
      <MenuItem value={item.id} key={item.id}>
        {item.name}
      </MenuItem>
    ));

  return (
    <StyledInfoWrapper>
      <StyledAddLabel key={1}>Add Transaction</StyledAddLabel>
      <StyledInfoContent>
        <StyledInfoDetail>
          <Formik
            initialValues={{
              name: "",
              parentId: "",
              type: categoryTypeList[0],
              iconId: Object.entries(DefaultCategory)[0][0],
            }}
            // validate form values
            validationSchema={Yup.object({
              name: Yup.string()
                .required("Category name is required")
                .max(20, "Category name cannot exceed 20 characters"),
            })}
            // handle submit form
            onSubmit={handleSubmitForm}
            innerRef={formRef}
          >
            {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
              <Form>
                <IconInput
                  onChange={handleChange}
                  name={"iconId"}
                  id={"iconId"}
                  value={values.iconId}
                />

                <FormItem
                  label={"Name"}
                  htmlFor="name"
                  hasError={!!(errors.name && touched.name)}
                  error={errors.name}
                  required
                  formItem={
                    <StyledFormInput
                      name={"name"}
                      id={"name"}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={"Enter wallet name"}
                      variant={"standard"}
                    />
                  }
                />

                <FormItem
                  label={"Transaction Type"}
                  htmlFor="type"
                  hasError={!!(errors.type && touched.type)}
                  error={errors.type}
                  formItem={
                    <StyledFormSelect
                      name={"type"}
                      id={"type"}
                      labelId="type"
                      value={values.type}
                      onChange={(event) => {
                        handleChange(event);
                        if (event.target.value) {
                          setCategoryType(
                            event.target
                              .value as (typeof categoryTypeList)[number]
                          );
                        }
                      }}
                      onBlur={handleBlur}
                    >
                      {typeList}
                    </StyledFormSelect>
                  }
                />

                <FormItem
                  label={"Parent Category"}
                  htmlFor="parentId"
                  hasError={!!(errors.parentId && touched.parentId)}
                  error={errors.parentId}
                  formItem={
                    <StyledFormSelect
                      name={"parentId"}
                      id={"parentId"}
                      labelId="parentId"
                      value={values.parentId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {categoryList}
                    </StyledFormSelect>
                  }
                />
                <If condition={dirty}>
                  <StyledUnsaved>Your form has unsaved fields!</StyledUnsaved>
                </If>
                <StyledFormAction>
                  <WarningButton
                    onClick={() => handleCancelForm(formRef, onCancel)}
                  >
                    Cancel
                  </WarningButton>
                  <Button type={"submit"} variant={"contained"}>
                    Save
                  </Button>
                </StyledFormAction>
              </Form>
            )}
          </Formik>
        </StyledInfoDetail>
      </StyledInfoContent>
    </StyledInfoWrapper>
  );
};

export default CategoryAdd;
